import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Sparkles, BookOpen, Users, Gift, ShieldCheck, SunMedium } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

const rulesOfLife = [
  'Quel est le langage d\'amour de mon époux / de mon épouse ?',
  'Quelles sont les 3 pratiques à mettre en priorité pour notre couple ?',
  'Quels sont les saints préférés de mon conjoint et de notre famille ?',
  'Quelle est la parole de la Bible qui nous inspire le plus ?',
  'Quelle est notre devise en couple ?',
  'Avons-nous écrit une charte de couple ?',
  'Quels sont les talents et les forces de chacun ?',
];

const dsaGroups = [
  {
    title: 'DSA 1 — Dire du bien',
    points: [
      'Regarder mon conjoint avec gratitude et lui dire ce que j\'aime chez lui.',
      'Reconnaître ses gestes de gentillesse, d\'aide et de délicatesse.',
      'Dire à mon conjoint que je l\'aime.',
    ],
  },
  {
    title: 'DSA 2 — Le dialogue dans le couple',
    points: [
      'Avoir des moments où nous sommes seuls pour parler sans distractions.',
      'Crée un espace de disponibilité et d\'écoute mutuelle.',
      'Évoquer les sujets sensibles sans tabou : tendresse, sexualité, famille, foi, projets...',
    ],
  },
  {
    title: 'DSA 3 — La tendresse',
    points: [
      'Comment je te regarde, avec quels yeux ?',
      'Comment je te parle, sur quel ton ?',
      'Quel geste concret vais-je faire pour manifester ma tendresse ?',
    ],
  },
  {
    title: 'DSA 4 — Le couple et internet',
    points: [
      'En quoi internet nous isole-t-il l\'un de l\'autre ?',
      'Quelles résolutions pour mieux concilier connexion et vie de couple ?',
      'Comment rendre notre relation plus présente, plus intime et plus simple ?',
    ],
  },
  {
    title: 'DSA 5 — Communiquer et percevoir',
    points: [
      'Lui ai-je fait mal dans mes manières de faire ou de dire ?',
      'Quels désirs ou besoins de mon conjoint puis-je deviner ?',
      'Qu\'est-ce que je peux faire pour mieux l\'écouter et le comprendre ?',
    ],
  },
];

const pemQuestions = [
  'Quelle a été la plus grande joie de mon conjoint depuis un mois ?',
  'Quel remerciement puis-je lui adresser ?',
  'Qu\'est-ce qui m\'a déçu ou blessé ?',
  'Quel est mon souci principal et le sien ?',
  'Qu\'est-ce qui ferait plaisir à mon conjoint ?',
  'Y a-t-il un pardon à demander ou à accorder ?',
  'Quel effort particulier ai-je fait pour améliorer notre couple ?',
  'Quelle place ai-je gardé à Jésus dans notre mariage ?',
];

const sevenDinners = [
  'La gratitude',
  'Rêver',
  'Ensemble',
  'Communiquer',
  'Nécessaire ou indispensable ?',
  'Vitaminez votre couple',
  'Venez à l\'écart',
];

const blasonItems = [
  'Ce que j\'aime et ce que nous aimons en couple',
  'Nos compétences et nos forces',
  'Ce que nous sommes : nos qualités et notre manière d\'être',
  'Nos alliés et personnes ressources',
];

const prayer = [
  'Toi qui as voulu que ton Verbe éternel naisse sur notre terre et grandisse dans une famille humaine...',
  'Envoie ton Esprit Saint pour qu\'Il donne à chacun la lumière de l\'Évangile.',
  'Donne à nos familles la grâce d\'être des lieux d\'amour, de vérité et de paix.',
  'Saint Joseph, gardien de la Sainte Famille, priez pour nous !',
];

const LoveDinnersPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false);

  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-white text-slate-800">
      <Header openContactModal={openContactModal} />

      <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />

      <div className="bg-gradient-to-r from-fsf-primary via-fsf-secondary to-fsf-primary py-16 shadow-lg">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center gap-3 text-white/90 mb-4">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm uppercase tracking-[0.25em] font-semibold">
              Les Associations Familiales Catholiques • Spécial Saint-Valentin
            </span>
          </div>

          <div className="max-w-5xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black font-serif tracking-tight mb-5">
              7 Dîners en amoureux
            </h1>
            <p className="text-justify text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Quiz, jeu, test : à chaque plat, un exercice pour se retrouver, mieux s\'aimer et renforcer le cœur de notre couple.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/10 px-5 py-3 font-semibold text-white transition hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à l’accueil
              </Link>
              <a
                href="#programme"
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-semibold text-fsf-secondary transition hover:scale-[1.01]"
              >
                <Heart className="w-4 h-4" />
                Voir le programme
              </a>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-12 md:px-6 lg:px-8">
        <section className="max-w-5xl mx-auto rounded-[28px] border border-amber-100 bg-white/90 p-6 md:p-8 shadow-xl backdrop-blur-sm">
          <div className="flex items-center gap-3 text-fsf-primary-dark mb-5">
            <Heart className="w-6 h-6" />
            <h2 className="text-2xl font-bold font-serif">Le cœur du programme</h2>
          </div>

          <p className="text-justify text-lg leading-8 text-slate-700">
            Aujourd’hui est un jour spécial, vous avez rendez-vous avec votre conjoint. Depuis le temps est passé,
            les enfants sont arrivés, la fatigue se fait parfois sentir et la distance parfois aussi. Il est temps de prendre
            du temps l’un pour l’autre pour vous réajuster, célébrer votre amour et retrouver la joie de vivre ensemble.
          </p>

          <blockquote className="mt-6 rounded-2xl border-l-4 border-fsf-secondary bg-orange-50 px-5 py-4 text-justify text-lg italic text-slate-700 shadow-sm">
            “Très bonne soirée !” — Pascale Morinière, Présidente nationale des AFC
          </blockquote>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl bg-amber-50 p-4 border border-amber-100">
              <p className="text-sm uppercase tracking-[0.2em] text-fsf-primary-dark font-semibold">Préparation</p>
              <p className="mt-2 text-justify text-slate-700">Mettez les téléphones en sourdine, évitez les distractions et réservez un moment rien que pour vous.</p>
            </div>
            <div className="rounded-2xl bg-orange-50 p-4 border border-orange-100">
              <p className="text-sm uppercase tracking-[0.2em] text-fsf-secondary font-semibold">Format</p>
              <p className="mt-2 text-justify text-slate-700">À chaque plat, une étape : entrée, plat, dessert. Une vraie soirée de dialogue et de partage.</p>
            </div>
            <div className="rounded-2xl bg-red-50 p-4 border border-red-100">
              <p className="text-sm uppercase tracking-[0.2em] text-rose-700 font-semibold">Objectif</p>
              <p className="mt-2 text-justify text-slate-700">Échanger, se réconcilier, rêver ensemble et redécouvrir le chemin de la tendresse.</p>
            </div>
          </div>
        </section>

        <section id="programme" className="mt-14 max-w-6xl mx-auto space-y-10">
          <div className="rounded-[28px] bg-white shadow-xl border border-orange-100 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-6 h-6 text-fsf-primary-dark" />
              <h2 className="text-3xl font-bold font-serif text-slate-800">26 idées de règles de vie</h2>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <table className="w-full text-justify text-sm md:text-base">
                <thead className="bg-gradient-to-r from-fsf-primary to-fsf-secondary text-white">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Règle de vie</th>
                    <th className="px-4 py-3 font-semibold">Jamais</th>
                    <th className="px-4 py-3 font-semibold">Parfois</th>
                    <th className="px-4 py-3 font-semibold">Souvent</th>
                    <th className="px-4 py-3 font-semibold">Très souvent</th>
                  </tr>
                </thead>
                <tbody>
                  {rulesOfLife.map((rule, index) => (
                    <tr key={rule} className={index % 2 === 0 ? 'bg-amber-50/40' : 'bg-white'}>
                      <td className="px-4 py-4 font-medium text-slate-700 text-justify">{rule}</td>
                      <td className="px-4 py-4 border-l border-slate-200" />
                      <td className="px-4 py-4 border-l border-slate-200" />
                      <td className="px-4 py-4 border-l border-slate-200" />
                      <td className="px-4 py-4 border-l border-slate-200" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-[28px] bg-gradient-to-br from-amber-50 to-orange-50 shadow-xl border border-orange-100 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-6 h-6 text-fsf-secondary" />
              <h2 className="text-3xl font-bold font-serif text-slate-800">Le Devoir de S'Asseoir (DSA)</h2>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {dsaGroups.map((group) => (
                <article key={group.title} className="rounded-2xl border border-amber-200 bg-white/80 p-5 shadow-sm">
                  <h3 className="text-xl font-bold text-fsf-primary-dark mb-3">{group.title}</h3>
                  <ul className="space-y-3 text-slate-700">
                    {group.points.map((point) => (
                      <li key={point} className="flex gap-2 text-justify">
                        <span className="mt-1 h-2.5 w-2.5 rounded-full bg-fsf-secondary shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>

          <div className="rounded-[28px] bg-white shadow-xl border border-slate-200 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="w-6 h-6 text-fsf-primary-dark" />
              <h2 className="text-3xl font-bold font-serif text-slate-800">Le Point En Ménage (PEM)</h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {pemQuestions.map((question, index) => (
                <div key={question} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-2 text-xs font-semibold tracking-[0.2em] uppercase text-fsf-secondary">Question {index + 1}</div>
                  <p className="text-justify text-slate-700 leading-7">{question}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[28px] bg-gradient-to-r from-[#fff4ed] to-[#fffaf5] shadow-xl border border-orange-100 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Gift className="w-6 h-6 text-fsf-primary-dark" />
              <h2 className="text-3xl font-bold font-serif text-slate-800">Votre couple en 10 questions</h2>
            </div>
            <p className="mb-5 text-justify text-slate-700">
              Répondez séparément à ce questionnaire, puis échangez vos livrets et discutez ensuite.
            </p>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl bg-amber-50 p-4">
                  <p className="font-semibold text-fsf-primary-dark">Très satisfait(e)</p>
                  <p className="text-slate-700">1</p>
                </div>
                <div className="rounded-xl bg-amber-50 p-4">
                  <p className="font-semibold text-fsf-primary-dark">Moyennement satisfait(e)</p>
                  <p className="text-slate-700">2</p>
                </div>
                <div className="rounded-xl bg-orange-50 p-4">
                  <p className="font-semibold text-fsf-secondary">Peu satisfait(e)</p>
                  <p className="text-slate-700">3</p>
                </div>
                <div className="rounded-xl bg-red-50 p-4">
                  <p className="font-semibold text-rose-700">Pas satisfait(e)</p>
                  <p className="text-slate-700">4</p>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
              <h3 className="text-xl font-bold text-fsf-primary-dark mb-3">Des pistes pour mieux t’aimer</h3>
              <ul className="space-y-3 text-justify text-slate-700">
                <li>• Ce qui m’émerveille chez toi, c’est…</li>
                <li>• Ce qui me demande des efforts pour t’aimer, c’est quand tu…</li>
                <li>• Je sais ce que je dois changer en moi pour te plaire…</li>
                <li>• Je sais que tu aimes quand je…</li>
              </ul>
            </div>
          </div>

          <div className="rounded-[28px] bg-white shadow-xl border border-orange-100 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <SunMedium className="w-6 h-6 text-fsf-secondary" />
              <h2 className="text-3xl font-bold font-serif text-slate-800">Les sept dîners</h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sevenDinners.map((dinner, index) => (
                <div key={dinner} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-fsf-secondary text-sm font-bold text-white">
                    {index + 1}
                  </span>
                  <span className="font-medium text-slate-700">{dinner}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[28px] bg-gradient-to-r from-[#fff7ed] to-[#fff0f0] shadow-xl border border-amber-100 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-6 h-6 text-fsf-primary-dark" />
              <h2 className="text-3xl font-bold font-serif text-slate-800">Quel est mon blason ? Quel serait notre blason ?</h2>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <div className="rounded-2xl border border-orange-200 bg-white p-5">
                <h3 className="text-2xl font-bold text-fsf-primary-dark mb-4">Mon blason</h3>
                <ul className="space-y-3 text-slate-700">
                  {blasonItems.map((item) => (
                    <li key={item} className="flex gap-2 text-justify">
                      <span className="mt-2 h-2 w-2 rounded-full bg-fsf-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-red-200 bg-white p-5">
                <h3 className="text-2xl font-bold text-rose-700 mb-4">Notre blason</h3>
                <ul className="space-y-3 text-slate-700">
                  {blasonItems.map((item) => (
                    <li key={`${item}-couple`} className="flex gap-2 text-justify">
                      <span className="mt-2 h-2 w-2 rounded-full bg-rose-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-[28px] bg-slate-900 text-white shadow-xl border border-slate-800 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-6 h-6 text-amber-300" />
              <h2 className="text-3xl font-bold font-serif">Prière des AFC</h2>
            </div>

            <div className="space-y-5 text-justify text-lg leading-8 text-slate-200">
              {prayer.map((sentence, index) => (
                <p key={sentence} className={index === prayer.length - 1 ? 'font-semibold text-amber-200' : ''}>
                  {sentence}
                </p>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LoveDinnersPage;
